import type { ToolApprovalCardProps, ToolApprovalRenderer } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { CalendarEventEditor, EDITABLE_FIELDS, OnlineMeetingSwitch, TIME_FIELDS, WhenFields } from "#features/conversations/components/prepared-action/CalendarEventEditor.tsx";
import { CalendarInviteDetails } from "#features/conversations/components/prepared-action/CalendarInviteDetails.tsx";
import { CalendarInviteRow } from "#features/conversations/components/prepared-action/CalendarInviteRow.tsx";
import { CalendarEventReadView } from "#features/conversations/components/prepared-action/CalendarEventReadView.tsx";
import { hasWallClockTimes, haveSameTimes, providerOf } from "#features/conversations/components/prepared-action/calendarEventFields.ts";
import { DraftSurface, EditErrors, useDraftMode } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { INTEGRATION_MANIFESTS } from "#features/conversations/integrations/index.ts";
import { readString } from "#features/conversations/lib/untypedFields.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Button, H3, Text } from "@hopper-ui/components";
import { IconCalendar, IconPencilBox } from "@intentui/icons";
import { useState } from "react";
import "./CalendarEventCard.css";

function CalendarEventCard(props: ToolApprovalCardProps) {
    const { args, display, edits, editErrors, siblings, onEditArgs } = props;
    const { tShared } = useSharedTranslation();
    const mode = useDraftMode();
    const isSettled = mode === "settled";
    // A rejected edit comes back open, so the field and its error are in front of the manager.
    const [isEditing, setIsEditing] = useState(editErrors.length > 0);
    // Only an Edit press moves focus into the form; a rejected edit opening the card leaves it where it is, like the composer.
    const [hasPressedEdit, setHasPressedEdit] = useState(false);
    const showsEditor = isEditing && !isSettled;
    // The read view shows what will be booked, edits included.
    const shown = { ...args, ...edits };
    const provider = providerOf(shown);
    const subject = readString(shown, "subject");
    const providerName = provider === null ? null : INTEGRATION_MANIFESTS[provider].displayName;
    const onlineControl = showsEditor ? <OnlineMeetingSwitch args={shown} isDisabled={mode !== "editing"} onChange={isOnline => onEditArgs({ is_online_event: isOnline })} /> : undefined;
    const whenControl = showsEditor ? <WhenFields {...props} isDisabled={mode !== "editing"} /> : undefined;
    const readView = <CalendarEventReadView args={shown} display={display ?? {}} hidesAvailability={!haveSameTimes(args, shown)} isSettled={isSettled} hidesDescription={showsEditor} siblings={siblings} onlineControl={onlineControl} whenControl={whenControl} />;
    const toggle = mode === "editing" && (
        <Button variant="ghost-secondary" size="xs" className="wai-pa-btn wai-pa-btn-plain" aria-expanded={isEditing} onPress={() => { setIsEditing(value => !value); setHasPressedEdit(true); }}>
            <IconPencilBox aria-hidden="true" />
            <Text>{tShared(isEditing ? "calendarEvent.done" : "calendarEvent.edit")}</Text>
        </Button>
    );

    return (
        <DraftSurface footerStatus={toggle}>
            <div className="wai-pa-header">
                <IconCalendar className="wai-pa-glyph" aria-hidden="true" />
                <Text size="xs" fontWeight="body-xs-medium" color="neutral-weak" className="wai-pa-label">
                    {providerName === null ? tShared("calendarEvent.invite") : tShared("calendarEvent.inviteWithProvider", { provider: providerName })}
                </Text>
            </div>
            {showsEditor ? (
                <CalendarEventEditor {...props} isDisabled={mode !== "editing"} focusesTitle={hasPressedEdit}>{readView}</CalendarEventEditor>
            ) : (
                <>
                    {subject !== null && <H3 fontSize="body-sm" fontWeight="body-sm-semibold" lineHeight="body-sm" color="neutral" className="wai-pa-title">{subject}</H3>}
                    {readView}
                </>
            )}
            <EditErrors errors={showsEditor ? editErrors.filter(error => error.field === null || ![...EDITABLE_FIELDS, ...TIME_FIELDS].includes(error.field)) : editErrors} />
        </DraftSurface>
    );
}

export const createCalendarEventRenderer: ToolApprovalRenderer = {
    matches: args => readString(args, "subject") !== null && hasWallClockTimes(args),
    Card: CalendarEventCard,
    decisionLabels: { allow: "calendarEvent.bookIt", deny: "preparedAction.discard" },
    batch: { Glyph: IconCalendar, heading: "calendarEvent.batchHeading", bookAll: "calendarEvent.bookAll", Row: CalendarInviteRow, Details: CalendarInviteDetails, title: args => readString(args, "subject") }
};
